import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { JobCardFormWrapper } from '../../components/forms/JobCardFormWrapper';
import { VehicleSelectorModal } from '../../components/modals/VehicleSelectorModal';
import { useAssignmentFlowStore } from '../../stores/assignmentFlowStore';
import { useJobCardContext } from '../../contexts/JobCardContext';
import { useNavigationContext } from '../../navigation/NavigationContext';
import { Vehicle } from '../../types';
import theme from '../../theme';
import { Ionicons } from '@expo/vector-icons';

export const AddJobCardScreen = ({ navigation, route }: any) => {
  const isWeb = Platform.OS === 'web';
  const { setCurrentRoute } = useNavigationContext();
  const { editingJobCard, setEditingJobCard } = useJobCardContext();
  // On web, use context; on mobile, use route params
  const editingCard = isWeb ? editingJobCard : (route?.params?.editingCard || null);
  const returnedVehicle = route?.params?.selectedVehicle || null; // Vehicle returned from receiving form (mobile)
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(returnedVehicle);
  const [showVehicleSelector, setShowVehicleSelector] = useState(!editingCard && !returnedVehicle);
  
  // Assignment flow store (for web navigation)
  const pendingAssignment = useAssignmentFlowStore((state) => state.pendingAssignment);
  const clearPendingAssignment = useAssignmentFlowStore((state) => state.clearPendingAssignment);

  console.log('🔍 AddJobCardScreen render - selectedVehicle:', selectedVehicle?.licensePlate, 'showModal:', showVehicleSelector);
  console.log('🔍 Pending assignment from store:', pendingAssignment?.vehicle?.licensePlate);

  // Check for pending assignment from store (web navigation)
  useEffect(() => {
    if (pendingAssignment && pendingAssignment.returnToJobCard && !selectedVehicle) {
      console.log('✅ Found pending assignment in store - auto-selecting vehicle:', pendingAssignment.vehicle.licensePlate);
      setSelectedVehicle(pendingAssignment.vehicle);
      setShowVehicleSelector(false);
      // Clear the pending assignment now that we've used it
      clearPendingAssignment();
    }
  }, [pendingAssignment, selectedVehicle, clearPendingAssignment]);

  // Handle vehicle returned from receiving form completion (mobile navigation with params)
  useEffect(() => {
    if (returnedVehicle) {
      console.log('✅ Received vehicle from receiving form (params):', returnedVehicle.licensePlate);
      setSelectedVehicle(returnedVehicle);
      setShowVehicleSelector(false);
    }
  }, [returnedVehicle]);

  const handleVehicleSelect = (vehicle: Vehicle) => {
    console.log('🚗 Vehicle selected:', vehicle.licensePlate, vehicle.makeModel);
    setSelectedVehicle(vehicle);
    setShowVehicleSelector(false);
    console.log('✅ Modal closed, form should now appear');
  };

  const handleChangeVehicle = () => {
    setShowVehicleSelector(true);
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => {
            if (isWeb) {
              setCurrentRoute('MaintenanceJobCards');
            } else {
              navigation?.goBack?.();
            }
          }}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>
          {editingCard ? 'Edit Job Card' : 'New Job Card'}
        </Text>
        <View style={styles.placeholder} />
      </View>

      {/* Show form only after vehicle is selected (or when editing) */}
      {(selectedVehicle || editingCard) ? (
        <JobCardFormWrapper 
          navigation={navigation}
          editingCard={editingCard}
          selectedVehicle={selectedVehicle}
          onChangeVehicle={handleChangeVehicle}
        />
      ) : (
        <View style={styles.waitingContainer}>
          <Ionicons name="car-sport-outline" size={64} color={theme.colors.gray300} />
          <Text style={styles.waitingText}>Select a vehicle to continue</Text>
        </View>
      )}

      {/* Vehicle Selector Modal */}
      <VehicleSelectorModal
        visible={showVehicleSelector}
        onClose={() => {
          // If no vehicle selected and not editing, go back
          if (!selectedVehicle && !editingCard) {
            if (isWeb) {
              setCurrentRoute('MaintenanceJobCards');
            } else {
              navigation?.goBack?.();
            }
          } else {
            setShowVehicleSelector(false);
          }
        }}
        onSelectVehicle={handleVehicleSelect}
        navigation={navigation}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray100,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: theme.typography.weights.bold as any,
    color: theme.colors.textPrimary,
  },
  placeholder: {
    width: 40,
  },
  waitingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing.xl,
  },
  waitingText: {
    marginTop: theme.spacing.md,
    fontSize: theme.typography.sizes.lg,
    color: theme.colors.textSecondary,
    textAlign: 'center' as const,
  },
});
